/**
 * registrationDraft — what a vehicle registration has to be before it can be saved.
 *
 * THE PLATE IS A MERGE FIELD AND THE GATE IS THE ROW. `sign_contract` clears its registration item
 * on the existence of a `vehicle_registrations` row, and the hire purchase agreement prints the
 * number from it — so a registration with no number is a green gate over an unprintable document.
 * The form this replaces enforced that with `required` on an input and a toast reading "Please
 * fill in all required fields", which named neither the field nor the reason.
 *
 * THE PAYMENT IS REQUIRED, and that is the column speaking rather than a preference:
 * `vehicle_registrations.transaction_id` is NOT NULL. The sheet this replaces asked for it on the
 * way in and then sent `transaction_id: <whatever the picker held>` on every edit too — so an
 * admin who cleared it got a constraint violation from Postgres instead of a sentence. Saying it
 * here says it in the admin's words, before the round trip.
 *
 * PURE, AND REACT-FREE — vitest runs node-env over `.ts` only.
 */

import { registrationTypeLabel } from '@/lib/registration';

/** The fields the registration group holds, as strings, exactly as the inputs hold them. */
export interface RegistrationDraft {
    type: string;
    number: string;
    /** `transactions.id` as a string — '' when nothing is linked. */
    transactionId: string;
}

/** The refusal for a registration that cannot be saved, or null. */
export function validateRegistrationDraft(draft: RegistrationDraft): string | null {
    if (draft.number.trim() === '') {
        return 'Enter the registration number — it prints on the hire purchase agreement.';
    }
    if (draft.type.trim() === '') return 'Choose how this vehicle was registered.';
    if (draft.transactionId.trim() === '') {
        return 'Link the payment for the registration — the record cannot be saved without one.';
    }
    return null;
}

/**
 * The collapsed summary row: `GT-1234-24 · New Registration`.
 *
 * AN UNREGISTERED CAR SAYS SO IN ITS OWN WORDS rather than showing an empty row, because empty is
 * the normal state for a fortnight and a blank line reads as a failed read.
 */
export function registrationSummary(draft: {
    number: string;
    type: string;
}): string {
    const number = draft.number.trim();
    if (!number) return 'Not registered yet';
    return [number, registrationTypeLabel(draft.type)].filter(Boolean).join(' · ');
}
